import { __, buildCleanUrl, copyToClipboard } from '../../utils';
import { HeaderIconButton } from './HeaderIconButton';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  useTheme,
  TextField,
  InputAdornment,
  Stack,
  ZdielanieIcon,
} from '@presov.sk/presov-ui-react';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { useLocation } from 'react-router';

const ShareButton: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  // Build a clean URL without tracking parameters
  const fullUrl = React.useMemo(() => {
    return buildCleanUrl(pathname, search, hash);
  }, [pathname, search, hash]);

  return (
    <>
      <HeaderIconButton
        onClick={handleOpen}
        aria-label='share'
        className='screen-only'
      >
        <ZdielanieIcon
          weight={2}
          sx={{ color: (theme) => theme.palette.primary.contrastText }}
        />
      </HeaderIconButton>
      <QRCodeSVG
        className='print-only'
        value={fullUrl}
        style={{
          width: theme.typography.pxToRem(64),
          height: theme.typography.pxToRem(64),
        }}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontSize: {
              xs: theme.typography.h4.fontSize,
              md: theme.typography.h3.fontSize,
            },
          }}
        >
          {__('Zdielať stránku')}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <QRCodeSVG
            value={fullUrl}
            style={{
              width: '100%',
              maxWidth: theme.typography.pxToRem(360),
              height: 'auto',
            }}
          />

          <Stack
            gap={1}
            sx={{
              width: '100%',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                disabled
                value={fullUrl}
                sx={{
                  '& .MuiInputBase-root': {
                    pr: 0,
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Button
                          aria-label='copy-url'
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                            borderRadius: 0,
                            minWidth: theme.typography.pxToRem(48),
                            width: theme.typography.pxToRem(48),
                          }}
                          onClick={() => {
                            copyToClipboard(fullUrl, {
                              onSuccess: () => {
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              },
                            });
                          }}
                        >
                          {copied ? (
                            <CheckIcon
                              sx={{
                                fontSize: 18,
                              }}
                            />
                          ) : (
                            <ContentCopyIcon
                              sx={{
                                fontSize: 18,
                              }}
                            />
                          )}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <Button
                fullWidth
                onClick={async () => {
                  try {
                    await navigator.share({
                      title:
                        typeof document !== 'undefined'
                          ? document.title
                          : 'Share',
                      url: fullUrl,
                    });
                    return;
                  } catch (_) {
                    // ignore and keep dialog open as fallback
                  }
                }}
              >
                {__('Zdieľať')}
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButton;
